/**
 * How much untrusted input is read before giving up.
 *
 * Real assessments are tens of kilobytes. The limits are far above that, and
 * exist so a mistaken or hostile file fails with a message instead of freezing
 * the tab while it is parsed or filling the browser's storage.
 */
export const MAX_REPORT_BYTES = 10 * 1024 * 1024
export const MAX_SESSION_BYTES = 100 * 1024 * 1024
/** Real reports carry tens of checks. */
export const MAX_CHECKS = 10_000
/**
 * Real reports nest a few levels deep. JSON.parse takes any depth, but
 * hashing, storing (structured clone) and exporting recurse, and a few
 * thousand levels overflow the stack.
 */
export const MAX_DEPTH = 64

/** Whether a parsed value nests deeper than `limit`, walked without recursion. */
export function deeperThan(value: unknown, limit: number): boolean {
  const stack: [unknown, number][] = [[value, 0]]
  while (stack.length) {
    const [item, depth] = stack.pop()!
    if (typeof item !== 'object' || item === null) continue
    if (depth >= limit) return true
    for (const child of Object.values(item)) stack.push([child, depth + 1])
  }
  return false
}

export class TooLarge extends Error {
  constructor(what: string, limit: number) {
    super(`${what} is larger than ${Math.round(limit / (1024 * 1024))} MB, the most this reads.`)
    this.name = 'TooLarge'
  }
}

export async function readFileJson(file: File, limit: number): Promise<unknown> {
  if (file.size > limit) throw new TooLarge(file.name, limit)
  return JSON.parse(await file.text())
}

/** How long one request may take, reading its body included. */
const FETCH_TIMEOUT_MS = 30_000

export class TimedOut extends Error {
  constructor(url: string) {
    let host = url
    try {
      host = new URL(url).host
    } catch {
      // Shown as given.
    }
    super(`${host} did not answer within ${FETCH_TIMEOUT_MS / 1000} seconds. Try again later.`)
    this.name = 'TimedOut'
  }
}

// By name: a DOMException is not an Error in every engine.
const isTimeout = (error: unknown) =>
  ['TimeoutError', 'AbortError'].includes((error as { name?: unknown } | null)?.name as string)

/**
 * Every request for data: no cookies or referrer go with it, and it gives up
 * after FETCH_TIMEOUT_MS. Without that, a server that answers a byte at a
 * time would hold a load, and every sync queued behind it, for ever. The
 * signal also covers the body, which readResponseJson reads.
 */
export async function fetchData(
  url: string,
  init: { headers?: Record<string, string> } = {},
  fetchImpl: typeof fetch = fetch,
): Promise<Response> {
  try {
    return await fetchImpl(url, {
      ...init,
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    })
  } catch (error) {
    throw isTimeout(error) ? new TimedOut(url) : error
  }
}

/**
 * Reads a response as JSON, stopping as soon as it passes the limit.
 *
 * Counted while streaming rather than trusted from Content-Length, which a
 * server may omit, or state for the compressed body.
 */
export async function readResponseJson(response: Response, limit: number): Promise<unknown> {
  try {
    return await readJson(response, limit)
  } catch (error) {
    throw isTimeout(error) ? new TimedOut(response.url) : error
  }
}

async function readJson(response: Response, limit: number): Promise<unknown> {
  const declared = Number(response.headers.get('content-length'))
  if (declared > limit) throw new TooLarge(response.url || 'The response', limit)
  if (!response.body) return JSON.parse(await response.text())

  const reader = response.body.getReader()
  const chunks: Uint8Array[] = []
  let received = 0
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    received += value.byteLength
    if (received > limit) {
      await reader.cancel()
      throw new TooLarge(response.url || 'The response', limit)
    }
    chunks.push(value)
  }
  const bytes = new Uint8Array(received)
  let offset = 0
  for (const chunk of chunks) {
    bytes.set(chunk, offset)
    offset += chunk.byteLength
  }
  return JSON.parse(new TextDecoder().decode(bytes))
}
